import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ClipboardCheck, Plus, Eye, Download, Trash2, AlertTriangle,
  CheckCircle, Clock, XCircle, Search, FilePlus2, ListChecks,
  Users, Calendar, Building2, FileWarning, Wrench, Edit, Send,
  ChevronLeft, ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPI = [
  { key: 'interno', label: 'Audit Interno', icon: Building2, color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  { key: 'esterno', label: 'Audit Esterno', icon: Users, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'ispezione_asl', label: 'Ispezione ASL', icon: AlertTriangle, color: 'bg-amber-50 text-amber-700 border-amber-200' },
];

const STATI = [
  { key: 'pianificato', label: 'Pianificato', color: 'bg-slate-100 text-slate-700' },
  { key: 'in_corso', label: 'In corso', color: 'bg-cyan-100 text-cyan-700' },
  { key: 'concluso', label: 'Concluso', color: 'bg-emerald-100 text-emerald-700' },
  { key: 'annullato', label: 'Annullato', color: 'bg-rose-100 text-rose-700' },
];

const ESITI = [
  { key: 'conforme', label: 'Conforme', color: 'bg-emerald-100 text-emerald-700' },
  { key: 'con_osservazioni', label: 'Con osservazioni', color: 'bg-amber-100 text-amber-700' },
  { key: 'non_conforme', label: 'Non Conforme', color: 'bg-rose-100 text-rose-700' },
];

const FINDING_TIPI = [
  { key: 'osservazione', label: 'Osservazione', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { key: 'NC_minore', label: 'NC Minore', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { key: 'NC_maggiore', label: 'NC Maggiore', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { key: 'critica', label: 'Critica', color: 'bg-rose-100 text-rose-700 border-rose-300' },
];

const FINDING_STATI = [
  { key: 'aperta', label: 'Aperta' },
  { key: 'in_trattamento', label: 'In trattamento' },
  { key: 'chiusa', label: 'Chiusa' },
];

const ACTION_STATI = [
  { key: 'pianificata', label: 'Pianificata' },
  { key: 'in_corso', label: 'In corso' },
  { key: 'completata', label: 'Completata' },
  { key: 'verificata_efficace', label: 'Verificata Efficace' },
  { key: 'verificata_non_efficace', label: 'Verificata NON Efficace' },
];

const emptyAudit = {
  tipo: 'interno',
  oggetto: '',
  ambito: '',
  procedure_collegate_ids: [],
  auditor_nome: '',
  auditor_organizzazione: '',
  auditor_email: '',
  auditees: [],
  data_pianificata: '',
  checklist: [],
};

const findLabel = (list, key) => list.find(x => x.key === key)?.label || key;
const findColor = (list, key) => list.find(x => x.key === key)?.color || '';

function StatusBadge({ value, list }) {
  const it = list.find(x => x.key === value);
  if (!it) return <span className="text-gray-400 text-xs">—</span>;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${it.color || ''}`}>{it.label}</span>;
}

export default function Audit({ user }) {
  const [audits, setAudits] = useState([]);
  const [stats, setStats] = useState({ totale: 0, pianificati: 0, in_corso: 0, conclusi: 0, nc_aperte: 0, azioni_in_scadenza: 0 });
  const [users, setUsers] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterStato, setFilterStato] = useState('all');
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAudit);
  const [wizardStep, setWizardStep] = useState(1);

  const [showDetail, setShowDetail] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState('checklist');

  const [showFinding, setShowFinding] = useState(false);
  const [findingForm, setFindingForm] = useState({ tipo: 'osservazione', descrizione: '', area: '', procedura_riferimento_id: '' });
  const [editingFinding, setEditingFinding] = useState(null);

  const [showAction, setShowAction] = useState(false);
  const [actionForm, setActionForm] = useState({ descrizione: '', responsabile_user_id: '', scadenza: '', finding_id: '' });
  const [editingAction, setEditingAction] = useState(null);

  const isSuperAdmin = user.role === 'super_amministratore';
  const isAdmin = isSuperAdmin || user.role === 'amministratore';

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [a, s, u, p] = await Promise.all([
        axios.get(`${API}/audits`, { headers: authHeaders }),
        axios.get(`${API}/audits/statistics`, { headers: authHeaders }),
        isAdmin ? axios.get(`${API}/users`, { headers: authHeaders }) : Promise.resolve({ data: [] }),
        axios.get(`${API}/procedure`, { headers: authHeaders }).catch(() => ({ data: [] })),
      ]);
      setAudits(a.data || []);
      setStats(s.data || {});
      setUsers(u.data || []);
      setProcedures(p.data || []);
    } catch (error) {
      console.error('Errore audit:', error);
      toast.error('Errore caricamento audit');
    }
  }, [authHeaders, isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    return audits.filter(a => {
      if (filterTipo !== 'all' && a.tipo !== filterTipo) return false;
      if (filterStato !== 'all' && a.stato !== filterStato) return false;
      if (search) {
        const s = search.toLowerCase();
        const txt = `${a.numero || ''} ${a.oggetto || ''} ${a.ambito || ''} ${a.auditor_nome || ''}`.toLowerCase();
        if (!txt.includes(s)) return false;
      }
      return true;
    });
  }, [audits, filterTipo, filterStato, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyAudit);
    setWizardStep(1);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      tipo: a.tipo,
      oggetto: a.oggetto || '',
      ambito: a.ambito || '',
      procedure_collegate_ids: a.procedure_collegate_ids || [],
      auditor_nome: a.auditor_nome || '',
      auditor_organizzazione: a.auditor_organizzazione || '',
      auditor_email: a.auditor_email || '',
      auditees: a.auditees || [],
      data_pianificata: a.data_pianificata || '',
      checklist: a.checklist || [],
    });
    setWizardStep(1);
    setShowForm(true);
  };

  const saveAudit = async () => {
    if (!form.oggetto || !form.auditor_nome) {
      toast.error('Oggetto e Auditor sono obbligatori');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/audits/${editing.id}`, form, { headers: authHeaders });
        toast.success('Audit aggiornato');
      } else {
        await axios.post(`${API}/audits`, form, { headers: authHeaders });
        toast.success('Audit creato');
      }
      setShowForm(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const deleteAudit = async (a) => {
    if (!window.confirm(`Eliminare l'audit ${a.numero}?`)) return;
    try {
      await axios.delete(`${API}/audits/${a.id}`, { headers: authHeaders });
      toast.success('Audit eliminato');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const downloadPdf = async (a) => {
    try {
      const res = await axios.get(`${API}/audits/${a.id}/pdf`, { headers: authHeaders, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_${a.numero}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore download PDF');
    }
  };

  const openDetail = async (a) => {
    try {
      const res = await axios.get(`${API}/audits/${a.id}`, { headers: authHeaders });
      setSelected(res.data);
      setDetailTab('checklist');
      setShowDetail(true);
    } catch (error) {
      toast.error('Errore apertura dettaglio');
    }
  };

  const reloadSelected = async () => {
    if (!selected) return;
    try {
      const res = await axios.get(`${API}/audits/${selected.id}`, { headers: authHeaders });
      setSelected(res.data);
      fetchAll();
    } catch (error) {
      console.error(error);
    }
  };

  const updateAuditField = async (patch) => {
    if (!selected) return;
    try {
      await axios.put(`${API}/audits/${selected.id}`, patch, { headers: authHeaders });
      reloadSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const updateChecklistItem = async (idx, field, value) => {
    if (!selected) return;
    const newList = [...(selected.checklist || [])];
    newList[idx] = { ...newList[idx], [field]: value };
    await updateAuditField({ checklist: newList });
  };

  // Findings
  const openAddFinding = () => {
    setEditingFinding(null);
    setFindingForm({ tipo: 'osservazione', descrizione: '', area: '', procedura_riferimento_id: '' });
    setShowFinding(true);
  };

  const openEditFinding = (f) => {
    setEditingFinding(f);
    setFindingForm({
      tipo: f.tipo,
      descrizione: f.descrizione || '',
      area: f.area || '',
      procedura_riferimento_id: f.procedura_riferimento_id || '',
      stato: f.stato,
      chiusura_note: f.chiusura_note || '',
    });
    setShowFinding(true);
  };

  const saveFinding = async () => {
    if (!findingForm.descrizione) { toast.error('Descrizione obbligatoria'); return; }
    try {
      const payload = { ...findingForm };
      if (!payload.procedura_riferimento_id) payload.procedura_riferimento_id = null;
      if (editingFinding) {
        await axios.put(`${API}/audits/${selected.id}/findings/${editingFinding.id}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API}/audits/${selected.id}/findings`, payload, { headers: authHeaders });
      }
      toast.success('Finding salvato');
      setShowFinding(false);
      reloadSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio finding');
    }
  };

  const deleteFinding = async (f) => {
    if (!window.confirm('Eliminare il finding?')) return;
    try {
      await axios.delete(`${API}/audits/${selected.id}/findings/${f.id}`, { headers: authHeaders });
      toast.success('Finding eliminato');
      reloadSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  // Actions
  const openAddAction = (findingId = '') => {
    setEditingAction(null);
    setActionForm({ descrizione: '', responsabile_user_id: '', scadenza: '', finding_id: findingId });
    setShowAction(true);
  };

  const openEditAction = (a) => {
    setEditingAction(a);
    setActionForm({
      descrizione: a.descrizione || '',
      responsabile_user_id: a.responsabile_user_id || '',
      scadenza: a.scadenza ? a.scadenza.substring(0, 10) : '',
      finding_id: a.finding_id || '',
      stato: a.stato,
      note_efficacia: a.note_efficacia || '',
    });
    setShowAction(true);
  };

  const saveAction = async () => {
    if (!actionForm.descrizione || !actionForm.responsabile_user_id || !actionForm.scadenza) {
      toast.error('Compila descrizione, responsabile e scadenza');
      return;
    }
    try {
      const payload = { ...actionForm };
      if (!payload.finding_id) payload.finding_id = null;
      if (editingAction) {
        await axios.put(`${API}/audits/${selected.id}/actions/${editingAction.id}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API}/audits/${selected.id}/actions`, payload, { headers: authHeaders });
      }
      toast.success('Azione salvata');
      setShowAction(false);
      reloadSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio azione');
    }
  };

  const deleteAction = async (a) => {
    if (!window.confirm("Eliminare l'azione?")) return;
    try {
      await axios.delete(`${API}/audits/${selected.id}/actions/${a.id}`, { headers: authHeaders });
      toast.success('Azione eliminata');
      reloadSelected();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const formatDate = (s) => {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString('it-IT'); } catch { return s; }
  };

  // Wizard - checklist editor
  const addChecklistItem = () => {
    setForm(f => ({ ...f, checklist: [...f.checklist, { categoria: '', domanda: '', risposta: null, note: '', procedura_id_link: null }] }));
  };
  const removeChecklistItem = (idx) => {
    setForm(f => ({ ...f, checklist: f.checklist.filter((_, i) => i !== idx) }));
  };
  const updateChecklistForm = (idx, field, value) => {
    setForm(f => {
      const lst = [...f.checklist];
      lst[idx] = { ...lst[idx], [field]: value };
      return { ...f, checklist: lst };
    });
  };

  const toggleAuditee = (uid) => {
    setForm(f => ({
      ...f,
      auditees: f.auditees.includes(uid) ? f.auditees.filter(x => x !== uid) : [...f.auditees, uid],
    }));
  };

  const toggleProcLink = (pid) => {
    setForm(f => ({
      ...f,
      procedure_collegate_ids: f.procedure_collegate_ids.includes(pid)
        ? f.procedure_collegate_ids.filter(x => x !== pid)
        : [...f.procedure_collegate_ids, pid],
    }));
  };

  // Quick template checklist
  const loadDefaultChecklist = () => {
    const def = [
      { categoria: 'Documentazione', domanda: 'Le procedure operative sono aggiornate e disponibili?', risposta: null, note: '' },
      { categoria: 'Documentazione', domanda: 'Le evidenze di lettura sono tracciate?', risposta: null, note: '' },
      { categoria: 'Formazione', domanda: 'Il personale ha completato la formazione obbligatoria?', risposta: null, note: '' },
      { categoria: 'Privacy', domanda: 'I documenti contenenti dati sensibili sono custoditi correttamente?', risposta: null, note: '' },
      { categoria: 'Sicurezza', domanda: 'Le attrezzature sono in stato di manutenzione conforme?', risposta: null, note: '' },
      { categoria: 'Igiene', domanda: 'Le procedure di igiene/sanificazione sono correttamente applicate?', risposta: null, note: '' },
    ];
    setForm(f => ({ ...f, checklist: [...f.checklist, ...def] }));
  };

  return (
    <div className="space-y-4" data-testid="audit-tab-content">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={ClipboardCheck} color="cyan" label="Totale" value={stats.totale} />
        <StatCard icon={Calendar} color="slate" label="Pianificati" value={stats.pianificati} />
        <StatCard icon={Clock} color="blue" label="In corso" value={stats.in_corso} />
        <StatCard icon={CheckCircle} color="emerald" label="Conclusi" value={stats.conclusi} />
        <StatCard icon={FileWarning} color="rose" label="NC Aperte" value={stats.nc_aperte} />
        <StatCard icon={AlertTriangle} color="amber" label="Az. in scadenza" value={stats.azioni_in_scadenza} />
      </div>

      {/* Filters + Action */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Cerca per numero, oggetto, auditor..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8"
            data-testid="audit-search-input"
          />
        </div>
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-[180px]" data-testid="audit-filter-tipo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {TIPI.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStato} onValueChange={setFilterStato}>
          <SelectTrigger className="w-[160px]" data-testid="audit-filter-stato"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {STATI.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-cyan-600 hover:bg-cyan-700" data-testid="audit-create-button">
            <Plus className="h-4 w-4 mr-1" />Nuovo Audit
          </Button>
        )}
      </div>

      {/* List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Numero</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Oggetto</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Tipo</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Auditor</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Pianif.</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Stato</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Esito</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-12 text-center text-gray-400">Nessun audit trovato</td></tr>
              )}
              {filtered.map(a => (
                <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`audit-row-${a.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-cyan-700">{a.numero}</td>
                  <td className="px-3 py-2"><div className="font-medium text-gray-900">{a.oggetto}</div>{a.ambito && <div className="text-xs text-gray-500">{a.ambito}</div>}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${findColor(TIPI, a.tipo)}`}>{findLabel(TIPI, a.tipo)}</span></td>
                  <td className="px-3 py-2 text-gray-700">{a.auditor_nome}{a.auditor_organizzazione ? ` (${a.auditor_organizzazione})` : ''}</td>
                  <td className="px-3 py-2 text-gray-600">{formatDate(a.data_pianificata)}</td>
                  <td className="px-3 py-2"><StatusBadge value={a.stato} list={STATI} /></td>
                  <td className="px-3 py-2"><StatusBadge value={a.esito} list={ESITI} /></td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(a)} data-testid={`audit-detail-${a.id}`} title="Dettaglio"><Eye className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(a)} title="PDF" data-testid={`audit-pdf-${a.id}`}><Download className="h-4 w-4" /></Button>
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => openEdit(a)} title="Modifica"><Edit className="h-4 w-4" /></Button>}
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => deleteAudit(a)} title="Elimina" className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Wizard Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="audit-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Modifica Audit ${editing.numero}` : 'Nuovo Audit'}</DialogTitle>
            <DialogDescription>Step {wizardStep} di 3 — {wizardStep === 1 ? 'Dati generali' : wizardStep === 2 ? 'Checklist' : 'Auditees & Procedure'}</DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-2">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex-1 h-1.5 rounded-full ${wizardStep >= s ? 'bg-cyan-500' : 'bg-gray-200'}`} />
            ))}
          </div>

          {wizardStep === 1 && (
            <div className="space-y-3">
              <div>
                <Label>Tipo *</Label>
                <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
                  <SelectTrigger data-testid="audit-form-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPI.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Oggetto / Titolo *</Label>
                <Input value={form.oggetto} onChange={e => setForm(f => ({ ...f, oggetto: e.target.value }))} placeholder="Es. Audit interno gestione documentale Q1 2026" data-testid="audit-form-oggetto" />
              </div>
              <div>
                <Label>Ambito</Label>
                <Textarea value={form.ambito} onChange={e => setForm(f => ({ ...f, ambito: e.target.value }))} placeholder="Aree/processi coinvolti" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Auditor (Nome) *</Label>
                  <Input value={form.auditor_nome} onChange={e => setForm(f => ({ ...f, auditor_nome: e.target.value }))} data-testid="audit-form-auditor" />
                </div>
                <div>
                  <Label>Organizzazione Auditor</Label>
                  <Input value={form.auditor_organizzazione} onChange={e => setForm(f => ({ ...f, auditor_organizzazione: e.target.value }))} />
                </div>
                <div>
                  <Label>Email Auditor</Label>
                  <Input type="email" value={form.auditor_email} onChange={e => setForm(f => ({ ...f, auditor_email: e.target.value }))} />
                </div>
                <div>
                  <Label>Data pianificata</Label>
                  <Input type="date" value={form.data_pianificata ? form.data_pianificata.substring(0, 10) : ''} onChange={e => setForm(f => ({ ...f, data_pianificata: e.target.value }))} />
                </div>
              </div>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">Definisci le domande della checklist</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={loadDefaultChecklist} type="button"><Plus className="h-3 w-3 mr-1" />Template base</Button>
                  <Button size="sm" onClick={addChecklistItem} type="button" className="bg-cyan-600 hover:bg-cyan-700"><Plus className="h-3 w-3 mr-1" />Domanda</Button>
                </div>
              </div>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
                {form.checklist.length === 0 && <p className="text-sm text-gray-400 italic text-center py-6">Nessuna domanda. Aggiungine una o usa il template.</p>}
                {form.checklist.map((it, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2" data-testid={`audit-checklist-row-${idx}`}>
                    <div className="grid grid-cols-12 gap-2">
                      <Input className="col-span-3" placeholder="Categoria" value={it.categoria || ''} onChange={e => updateChecklistForm(idx, 'categoria', e.target.value)} />
                      <Input className="col-span-8" placeholder="Domanda" value={it.domanda || ''} onChange={e => updateChecklistForm(idx, 'domanda', e.target.value)} />
                      <Button variant="ghost" size="sm" onClick={() => removeChecklistItem(idx)} className="col-span-1 text-rose-600" type="button"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {wizardStep === 3 && (
            <div className="space-y-4">
              <div>
                <Label className="mb-2 block">Auditees (utenti coinvolti)</Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50 space-y-1">
                  {users.length === 0 && <p className="text-sm text-gray-400">Nessun utente disponibile</p>}
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                      <Checkbox checked={form.auditees.includes(u.id)} onCheckedChange={() => toggleAuditee(u.id)} />
                      <span className="text-sm">{u.first_name} {u.last_name} <span className="text-xs text-gray-500">({u.role})</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Procedure di riferimento</Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50 space-y-1">
                  {procedures.length === 0 && <p className="text-sm text-gray-400">Nessuna procedura disponibile</p>}
                  {procedures.map(p => (
                    <label key={p.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                      <Checkbox checked={form.procedure_collegate_ids.includes(p.id)} onCheckedChange={() => toggleProcLink(p.id)} />
                      <span className="text-sm"><span className="font-mono text-xs text-cyan-700">{p.codice}</span> {p.titolo}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between sm:justify-between">
            <div>
              {wizardStep > 1 && <Button variant="outline" onClick={() => setWizardStep(wizardStep - 1)}><ChevronLeft className="h-4 w-4 mr-1" />Indietro</Button>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
              {wizardStep < 3 ? (
                <Button onClick={() => setWizardStep(wizardStep + 1)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="audit-form-next">Avanti<ChevronRight className="h-4 w-4 ml-1" /></Button>
              ) : (
                <Button onClick={saveAudit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="audit-form-save">{editing ? 'Aggiorna' : 'Crea Audit'}</Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="audit-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono text-cyan-700">{selected.numero}</span>
                  <span>·</span>
                  <span>{selected.oggetto}</span>
                </DialogTitle>
                <DialogDescription className="flex flex-wrap items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${findColor(TIPI, selected.tipo)}`}>{findLabel(TIPI, selected.tipo)}</span>
                  <StatusBadge value={selected.stato} list={STATI} />
                  {selected.esito && <StatusBadge value={selected.esito} list={ESITI} />}
                </DialogDescription>
              </DialogHeader>

              {/* Stato/Esito controls (admin) */}
              {isAdmin && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Cambia Stato</Label>
                    <Select value={selected.stato} onValueChange={v => {
                      const patch = { stato: v };
                      if (v === 'in_corso' && !selected.data_inizio) patch.data_inizio = new Date().toISOString();
                      if (v === 'concluso' && !selected.data_fine) patch.data_fine = new Date().toISOString();
                      updateAuditField(patch);
                    }}>
                      <SelectTrigger data-testid="audit-detail-stato"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATI.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Esito</Label>
                    <Select value={selected.esito || ''} onValueChange={v => updateAuditField({ esito: v })}>
                      <SelectTrigger data-testid="audit-detail-esito"><SelectValue placeholder="Seleziona esito" /></SelectTrigger>
                      <SelectContent>{ESITI.map(e => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={() => downloadPdf(selected)} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="audit-detail-pdf-btn"><Download className="h-4 w-4 mr-1" />Scarica PDF</Button>
                  </div>
                </div>
              )}

              <Tabs value={detailTab} onValueChange={setDetailTab} className="mt-3">
                <TabsList>
                  <TabsTrigger value="checklist" data-testid="audit-tab-checklist"><ListChecks className="h-4 w-4 mr-1" />Checklist ({selected.checklist?.length || 0})</TabsTrigger>
                  <TabsTrigger value="findings" data-testid="audit-tab-findings"><FileWarning className="h-4 w-4 mr-1" />Findings ({selected.findings?.length || 0})</TabsTrigger>
                  <TabsTrigger value="actions" data-testid="audit-tab-actions"><Wrench className="h-4 w-4 mr-1" />Azioni ({selected.actions?.length || 0})</TabsTrigger>
                  <TabsTrigger value="info" data-testid="audit-tab-info">Info</TabsTrigger>
                </TabsList>

                {/* Checklist Tab */}
                <TabsContent value="checklist" className="space-y-2 pt-3">
                  {(selected.checklist || []).length === 0 && <p className="text-sm text-gray-400 italic text-center py-6">Nessuna domanda checklist</p>}
                  {(selected.checklist || []).map((it, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">{it.categoria || 'Generale'}</div>
                      <div className="font-medium text-gray-900 mb-2">{it.domanda}</div>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {['si', 'no', 'na'].map(opt => (
                          <button
                            key={opt}
                            disabled={!isAdmin}
                            onClick={() => updateChecklistItem(idx, 'risposta', opt)}
                            className={`px-3 py-1 text-xs rounded-full font-semibold transition border ${
                              it.risposta === opt
                                ? opt === 'si' ? 'bg-emerald-600 text-white border-emerald-600' : opt === 'no' ? 'bg-rose-600 text-white border-rose-600' : 'bg-gray-600 text-white border-gray-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                            } ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                            data-testid={`audit-checklist-${idx}-${opt}`}
                          >{opt.toUpperCase()}</button>
                        ))}
                      </div>
                      {isAdmin ? (
                        <Input placeholder="Note" value={it.note || ''} onChange={e => updateChecklistItem(idx, 'note', e.target.value)} className="text-sm" />
                      ) : (
                        it.note && <div className="text-xs text-gray-600 italic">{it.note}</div>
                      )}
                    </div>
                  ))}
                </TabsContent>

                {/* Findings Tab */}
                <TabsContent value="findings" className="space-y-2 pt-3">
                  {isAdmin && <Button onClick={openAddFinding} className="bg-cyan-600 hover:bg-cyan-700" data-testid="audit-add-finding"><Plus className="h-4 w-4 mr-1" />Nuovo Finding</Button>}
                  {(selected.findings || []).length === 0 && <p className="text-sm text-gray-400 italic text-center py-6">Nessun finding registrato</p>}
                  {(selected.findings || []).map(f => (
                    <div key={f.id} className="border border-gray-200 rounded-lg p-3 bg-white" data-testid={`audit-finding-${f.id}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${findColor(FINDING_TIPI, f.tipo)}`}>{findLabel(FINDING_TIPI, f.tipo)}</span>
                            <span className="text-xs text-gray-500">Stato: <b>{findLabel(FINDING_STATI, f.stato)}</b></span>
                            {f.area && <span className="text-xs text-gray-500">Area: {f.area}</span>}
                          </div>
                          <p className="text-sm text-gray-800">{f.descrizione}</p>
                          {f.chiusura_note && <p className="text-xs text-gray-600 italic mt-1">Chiusura: {f.chiusura_note}</p>}
                          <p className="text-xs text-gray-400 mt-1">Aperta: {formatDate(f.created_at)} {f.chiusura_at && `• Chiusa: ${formatDate(f.chiusura_at)}`}</p>
                        </div>
                        {isAdmin && (
                          <div className="flex flex-col gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditFinding(f)}><Edit className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => openAddAction(f.id)} title="Crea azione"><Wrench className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteFinding(f)} className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </TabsContent>

                {/* Actions Tab */}
                <TabsContent value="actions" className="space-y-2 pt-3">
                  {isAdmin && <Button onClick={() => openAddAction()} className="bg-cyan-600 hover:bg-cyan-700" data-testid="audit-add-action"><Plus className="h-4 w-4 mr-1" />Nuova Azione</Button>}
                  {(selected.actions || []).length === 0 && <p className="text-sm text-gray-400 italic text-center py-6">Nessuna azione correttiva</p>}
                  {(selected.actions || []).map(a => {
                    const isResp = a.responsabile_user_id === user.id;
                    return (
                      <div key={a.id} className="border border-gray-200 rounded-lg p-3 bg-white" data-testid={`audit-action-${a.id}`}>
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{a.descrizione}</p>
                            <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-3">
                              <span><b>Resp:</b> {a.responsabile_user_name || '—'}</span>
                              <span><b>Scadenza:</b> {formatDate(a.scadenza)}</span>
                              <span><b>Stato:</b> {findLabel(ACTION_STATI, a.stato)}</span>
                            </div>
                            {a.note_efficacia && <p className="text-xs italic text-gray-600 mt-1">Efficacia: {a.note_efficacia}</p>}
                          </div>
                          {(isAdmin || isResp) && (
                            <div className="flex flex-col gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openEditAction(a)}><Edit className="h-4 w-4" /></Button>
                              {isAdmin && <Button size="sm" variant="ghost" onClick={() => deleteAction(a)} className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </TabsContent>

                {/* Info Tab */}
                <TabsContent value="info" className="pt-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <InfoRow label="Auditor" value={`${selected.auditor_nome || '—'}${selected.auditor_organizzazione ? ` (${selected.auditor_organizzazione})` : ''}`} />
                    <InfoRow label="Email Auditor" value={selected.auditor_email || '—'} />
                    <InfoRow label="Pianificato" value={formatDate(selected.data_pianificata)} />
                    <InfoRow label="Inizio" value={formatDate(selected.data_inizio)} />
                    <InfoRow label="Fine" value={formatDate(selected.data_fine)} />
                    <InfoRow label="Creato da" value={selected.created_by_name || '—'} />
                    <InfoRow label="Ambito" value={selected.ambito || '—'} full />
                  </div>
                  {isAdmin && (
                    <div className="mt-4">
                      <Label>Note finali</Label>
                      <Textarea
                        rows={3}
                        defaultValue={selected.note_finali || ''}
                        onBlur={e => {
                          if (e.target.value !== (selected.note_finali || '')) {
                            updateAuditField({ note_finali: e.target.value });
                          }
                        }}
                        data-testid="audit-note-finali"
                      />
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Finding Dialog */}
      <Dialog open={showFinding} onOpenChange={setShowFinding}>
        <DialogContent data-testid="audit-finding-dialog">
          <DialogHeader>
            <DialogTitle>{editingFinding ? 'Modifica Finding' : 'Nuovo Finding'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo *</Label>
              <Select value={findingForm.tipo} onValueChange={v => setFindingForm(f => ({ ...f, tipo: v }))}>
                <SelectTrigger data-testid="finding-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{FINDING_TIPI.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descrizione *</Label>
              <Textarea rows={3} value={findingForm.descrizione} onChange={e => setFindingForm(f => ({ ...f, descrizione: e.target.value }))} data-testid="finding-descrizione" />
            </div>
            <div>
              <Label>Area</Label>
              <Input value={findingForm.area} onChange={e => setFindingForm(f => ({ ...f, area: e.target.value }))} placeholder="Es. Documentazione, Igiene..." />
            </div>
            <div>
              <Label>Procedura collegata</Label>
              <Select value={findingForm.procedura_riferimento_id || 'none'} onValueChange={v => setFindingForm(f => ({ ...f, procedura_riferimento_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna</SelectItem>
                  {procedures.map(p => <SelectItem key={p.id} value={p.id}>{p.codice} — {p.titolo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editingFinding && (
              <>
                <div>
                  <Label>Stato</Label>
                  <Select value={findingForm.stato || editingFinding.stato} onValueChange={v => setFindingForm(f => ({ ...f, stato: v }))}>
                    <SelectTrigger data-testid="finding-stato"><SelectValue /></SelectTrigger>
                    <SelectContent>{FINDING_STATI.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Note di chiusura</Label>
                  <Textarea rows={2} value={findingForm.chiusura_note || ''} onChange={e => setFindingForm(f => ({ ...f, chiusura_note: e.target.value }))} />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinding(false)}>Annulla</Button>
            <Button onClick={saveFinding} className="bg-cyan-600 hover:bg-cyan-700" data-testid="finding-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={showAction} onOpenChange={setShowAction}>
        <DialogContent data-testid="audit-action-dialog">
          <DialogHeader>
            <DialogTitle>{editingAction ? 'Modifica Azione' : 'Nuova Azione Correttiva'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Descrizione *</Label>
              <Textarea rows={3} value={actionForm.descrizione} onChange={e => setActionForm(f => ({ ...f, descrizione: e.target.value }))} data-testid="action-descrizione" />
            </div>
            <div>
              <Label>Responsabile *</Label>
              <Select value={actionForm.responsabile_user_id} onValueChange={v => setActionForm(f => ({ ...f, responsabile_user_id: v }))}>
                <SelectTrigger data-testid="action-responsabile"><SelectValue placeholder="Seleziona utente" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scadenza *</Label>
              <Input type="date" value={actionForm.scadenza ? actionForm.scadenza.substring(0, 10) : ''} onChange={e => setActionForm(f => ({ ...f, scadenza: e.target.value }))} data-testid="action-scadenza" />
            </div>
            <div>
              <Label>Finding collegato</Label>
              <Select value={actionForm.finding_id || 'none'} onValueChange={v => setActionForm(f => ({ ...f, finding_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {(selected?.findings || []).map(f => <SelectItem key={f.id} value={f.id}>{findLabel(FINDING_TIPI, f.tipo)} — {f.descrizione.substring(0, 50)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editingAction && (
              <>
                <div>
                  <Label>Stato</Label>
                  <Select value={actionForm.stato || editingAction.stato} onValueChange={v => setActionForm(f => ({ ...f, stato: v }))}>
                    <SelectTrigger data-testid="action-stato"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ACTION_STATI.map(s => {
                        // Responsabile non admin: solo in_corso o completata
                        const isResp = editingAction.responsabile_user_id === user.id && !isAdmin;
                        if (isResp && !['in_corso', 'completata'].includes(s.key)) return null;
                        return <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                {isAdmin && (
                  <div>
                    <Label>Note efficacia</Label>
                    <Textarea rows={2} value={actionForm.note_efficacia || ''} onChange={e => setActionForm(f => ({ ...f, note_efficacia: e.target.value }))} />
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAction(false)}>Annulla</Button>
            <Button onClick={saveAction} className="bg-cyan-600 hover:bg-cyan-700" data-testid="action-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  const colorMap = {
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <div className={`rounded-lg p-3 border ${colorMap[color] || colorMap.slate}`} data-testid={`audit-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide opacity-75">{label}</p>
          <p className="text-2xl font-bold mt-0.5">{value ?? 0}</p>
        </div>
        <Icon className="h-6 w-6 opacity-60" />
      </div>
    </div>
  );
}

function InfoRow({ label, value, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5">{value || '—'}</p>
    </div>
  );
}
